/**
 * CollectionFilters - Integrated filter bar for collection records
 *
 * Atomic level: Organism
 * Composes: Select, Popover, Badge, Button, Input
 *
 * Provides filters for conduct views, conduct score range (1-10), and date range
 */

import React from 'react';
import { X, Filter, Calendar } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Input } from '../../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../ui/popover';
import { Separator } from '../../../ui/separator';
import { ConductScoreIndicator } from './ConductScoreIndicator';
import { cn } from '../../../../lib/utils';

export type ConductView = 'all' | 'dissatisfaction' | 'vulnerability' | 'consumer_duty';

export interface CollectionFiltersState {
  conductView: ConductView;
  conductScoreMin: number; // 1-10 scale
  conductScoreMax: number; // 1-10 scale
  dateFrom: string;
  dateTo: string;
}

export interface CollectionFiltersProps {
  filters: CollectionFiltersState;
  onFiltersChange: (filters: CollectionFiltersState) => void;
  className?: string;
  inline?: boolean; // If true, only show filter controls (no wrapper, no chips)
  showOnlyChips?: boolean; // If true, only show active filter chips
}

const defaultFilters: CollectionFiltersState = {
  conductView: 'all',
  conductScoreMin: 1,
  conductScoreMax: 10,
  dateFrom: '',
  dateTo: '',
};

const CONDUCT_VIEW_OPTIONS: { value: ConductView; label: string }[] = [
  { value: 'all', label: 'All Views' },
  { value: 'dissatisfaction', label: 'Dissatisfaction' },
  { value: 'vulnerability', label: 'Vulnerability' },
  { value: 'consumer_duty', label: 'Consumer Duty' },
];

const QUICK_DATE_RANGES = [
  { label: 'Today', days: 0 },
  { label: 'This Week', days: 7 },
  { label: 'This Month', days: 30 },
  { label: 'This Year', days: 365 },
];

const CollectionFilters: React.FC<CollectionFiltersProps> = ({
  filters,
  onFiltersChange,
  className,
  inline = false,
  showOnlyChips = false,
}) => {
  const hasActiveFilters =
    filters.conductView !== 'all' ||
    filters.conductScoreMin > 1 ||
    filters.conductScoreMax < 10 ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '';

  const activeFilterChips: { key: string; label: string }[] = [];

  if (filters.conductView !== 'all') {
    const viewLabel = CONDUCT_VIEW_OPTIONS.find((v) => v.value === filters.conductView)?.label || filters.conductView;
    activeFilterChips.push({ key: 'conductView', label: `View: ${viewLabel}` });
  }

  if (filters.conductScoreMin > 1 || filters.conductScoreMax < 10) {
    activeFilterChips.push({
      key: 'conductScore',
      label: `Conduct Score: ${filters.conductScoreMin}-${filters.conductScoreMax}`,
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const from = filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString() : '';
    const to = filters.dateTo ? new Date(filters.dateTo).toLocaleDateString() : '';
    activeFilterChips.push({
      key: 'date',
      label: `Date: ${from}${from && to ? ' - ' : ''}${to}`,
    });
  }

  const removeFilter = (key: string) => {
    const updated = { ...filters };
    switch (key) {
      case 'conductView':
        updated.conductView = 'all';
        break;
      case 'conductScore':
        updated.conductScoreMin = 1;
        updated.conductScoreMax = 10;
        break;
      case 'date':
        updated.dateFrom = '';
        updated.dateTo = '';
        break;
    }
    onFiltersChange(updated);
  };

  const clearAllFilters = () => {
    onFiltersChange(defaultFilters);
  };

  const applyQuickDateRange = (days: number) => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - days);
    
    onFiltersChange({
      ...filters,
      dateFrom: fromDate.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0],
    });
  };

  // If only showing chips, render just the chips
  if (showOnlyChips) {
    if (activeFilterChips.length === 0) return null;
    return (
      <div className={cn('flex items-center gap-2 flex-wrap', className)}>
        <span className="text-sm text-muted-foreground">Active filters:</span>
        {activeFilterChips.map((chip) => (
          <Badge
            key={chip.key}
            variant="secondary"
            className="gap-1 pr-1 cursor-pointer hover:bg-muted"
            onClick={() => removeFilter(chip.key)}
          >
            {chip.label}
            <X className="w-3 h-3" />
          </Badge>
        ))}
      </div>
    );
  }

  // Filter controls (conduct score and date only, conduct view handled separately)
  const filterControls = (
    <>
      {/* Conduct Score Range Filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9 relative" title="Conduct Score filter">
            <Filter className="w-4 h-4" />
            {(filters.conductScoreMin > 1 || filters.conductScoreMax < 10) && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center">
                {filters.conductScoreMax < 10 ? '!' : ''}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Conduct Score Range (1-10)</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={filters.conductScoreMin}
                  onChange={(e) =>
                    onFiltersChange({
                      ...filters,
                      conductScoreMin: Math.min(Math.max(Number(e.target.value) || 1, 1), filters.conductScoreMax),
                    })
                  }
                  className="h-9 w-16 text-sm"
                  placeholder="Min"
                />
                <ConductScoreIndicator score={filters.conductScoreMin} size="small" />
                <span className="text-muted-foreground text-sm">to</span>
                <ConductScoreIndicator score={filters.conductScoreMax} size="small" />
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={filters.conductScoreMax}
                  onChange={(e) =>
                    onFiltersChange({
                      ...filters,
                      conductScoreMax: Math.max(Math.min(Number(e.target.value) || 10, 10), filters.conductScoreMin),
                    })
                  }
                  className="h-9 w-16 text-sm"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Date Range Filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9 relative" title="Date range filter">
            <Calendar className="w-4 h-4" />
            {(filters.dateFrom || filters.dateTo) && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center">
                !
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Date Range</h4>
            
            {/* Quick Select Buttons */}
            <div className="flex flex-wrap gap-2">
              {QUICK_DATE_RANGES.map((range) => (
                <Button
                  key={range.label}
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => applyQuickDateRange(range.days)}
                >
                  {range.label}
                </Button>
              ))}
            </div>

            <Separator />

            {/* Custom Date Inputs */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">From</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  onFiltersChange({ ...filters, dateFrom: e.target.value })
                }
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">To</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  onFiltersChange({ ...filters, dateTo: e.target.value })
                }
                className="h-9 text-sm"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );

  // If inline mode, just return the controls
  if (inline) {
    return <div className={cn('flex items-center gap-2', className)}>{filterControls}</div>;
  }

  // Full layout with wrapper and chips
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Filter Controls Row */}
      <div className="flex flex-wrap items-center gap-2 flex-1">
        {/* Conduct Views Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Conduct View:</span>
          <Select
            value={filters.conductView}
            onValueChange={(v) => onFiltersChange({ ...filters, conductView: v as ConductView })}
          >
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="All Views" />
            </SelectTrigger>
            <SelectContent>
              {CONDUCT_VIEW_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filterControls}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Clear All Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-9 text-sm"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Active Filter Chips */}
      {activeFilterChips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {activeFilterChips.map((chip) => (
            <Badge
              key={chip.key}
              variant="secondary"
              className="gap-1 pr-1 cursor-pointer hover:bg-muted"
              onClick={() => removeFilter(chip.key)}
            >
              {chip.label}
              <X className="w-3 h-3" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export { CollectionFilters, defaultFilters };
